import React, { useState } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * SidebarLayout: Responsive sidebar that becomes a dropdown sheet on mobile.
 * - Desktop: collapsible sidebar on the left
 * - Mobile: full-width content with a dropdown nav bar at top
 * 
 * Props:
 *   title - sidebar header title
 *   icon - header icon component
 *   groups - [{label, items: [{id, label, icon, badge?}]}]
 *   activeId - currently selected item id
 *   onSelect - (id) => void
 *   children - main content
 *   footer - optional sidebar footer content
 *   accentColor - optional color for active items
 */
export default function SidebarLayout({ title, icon: TitleIcon, groups, activeId, onSelect, children, footer, accentColor }) {
  const [collapsed, setCollapsed] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Close mobile dropdown when activeId changes (e.g., bottom nav click)
  React.useEffect(() => {
    setMobileOpen(false);
  }, [activeId]);

  const allItems = groups.flatMap(g => g.items);
  const activeItem = allItems.find(i => i.id === activeId);
  const activeLabel = activeItem?.label || activeId;

  const handleSelect = (id) => {
    onSelect(id);
    setMobileOpen(false);
    if (isMobile) setCollapsed(true);
  };

  return (
    <div className="flex flex-col md:flex-row h-full overflow-hidden pb-mobile-nav" data-testid="sidebar-layout">
      {/* Mobile: dropdown nav bar */}
      <div className="md:hidden shrink-0 border-b bg-card/80">
        <button onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left active:bg-muted/60"
          data-testid="mobile-nav-toggle">
          {TitleIcon && <TitleIcon className="h-4 w-4 text-muted-foreground shrink-0" />}
          <span className="text-sm font-semibold flex-1">{activeLabel}</span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Mobile sheet overlay */}
        {mobileOpen && (
          <>
            <div className="fixed inset-0 z-[60] bg-black/30" onClick={() => setMobileOpen(false)} />
            <div className="fixed left-0 right-0 z-[70] bg-card border-b rounded-b-2xl shadow-xl max-h-[80vh] flex flex-col animate-in slide-in-from-top-2 duration-200" style={{ top: 'auto' }}>
              <div className="flex items-center justify-between px-4 py-2 border-b shrink-0">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</span>
                <button onClick={() => setMobileOpen(false)} className="p-1 rounded-md hover:bg-muted/60"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-2 space-y-3 pb-6">
                  {groups.map(group => (
                    <div key={group.label}>
                      <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-1">{group.label}</p>
                      <div className="space-y-0.5">
                        {group.items.map(item => {
                          const Icon = item.icon;
                          const isActive = item.id === activeId;
                          return (
                            <button key={item.id} onClick={() => handleSelect(item.id)}
                              data-testid={`mobile-nav-item-${item.id}`}
                              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                                isActive ? 'bg-primary/10 font-medium' : 'active:bg-muted/60'
                              }`}>
                              {Icon && <Icon className="h-4 w-4 shrink-0" style={isActive && accentColor ? { color: accentColor } : {}} />}
                              <span className="text-sm flex-1">{item.label}</span>
                              {item.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold">{item.badge}</span>}
                              {isActive && <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Desktop: collapsible sidebar */}
      <div className={`hidden md:flex border-r bg-muted/20 flex-col shrink-0 transition-all duration-200 ${collapsed ? 'w-12' : 'w-52'}`}>
        <div className="p-2 border-b flex items-center justify-between">
          {!collapsed && TitleIcon && (
            <h3 className="text-xs font-bold flex items-center gap-1.5 px-2" style={{ fontFamily: 'Space Grotesk' }}>
              <TitleIcon className="h-3.5 w-3.5" /> {title}
            </h3>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground">
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
        </div>
        <ScrollArea className="flex-1">
          <div className={`${collapsed ? 'p-1' : 'p-2'} space-y-3`}>
            {groups.map(group => (
              <div key={group.label}>
                {!collapsed && <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-1">{group.label}</p>}
                <div className="space-y-0.5">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const isActive = item.id === activeId;
                    return (
                      <button key={item.id} onClick={() => handleSelect(item.id)}
                        title={collapsed ? item.label : undefined}
                        data-testid={`sidebar-nav-${item.id}`}
                        className={`w-full text-left flex items-center ${collapsed ? 'justify-center px-1 py-2' : 'gap-2.5 px-3 py-1.5'} rounded-md transition-colors text-sm relative ${
                          isActive ? 'bg-primary/10 text-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                        }`}
                        style={isActive && accentColor ? { backgroundColor: `${accentColor}15`, color: accentColor } : {}}>
                        {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
                        {!collapsed && <span className="truncate text-xs">{item.label}</span>}
                        {item.badge && !collapsed && <span className="absolute top-0.5 right-1 text-[7px] px-1 rounded-full bg-red-500 text-white font-bold">{item.badge}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        {footer && !collapsed && <div className="p-2 border-t">{footer}</div>}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto">
        {children}
      </div>
    </div>
  );
}
